import React, { useState, useMemo } from "react";
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButton,
  IonButtons,
  IonBackButton,
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardContent,
  IonItem,
  IonLabel,
  IonInput,
  IonSelect,
  IonSelectOption,
  IonModal,
  IonIcon,
  IonLoading,
  IonAlert,
  IonGrid,
  IonRow,
  IonCol,
  IonChip,
  IonFab,
  IonFabButton,
} from "@ionic/react";
import {
  addOutline,
  createOutline,
  trashOutline,
  colorPaletteOutline,
  closeOutline,
} from "ionicons/icons";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { categoriesApi, categoryTagsApi } from "../api/categories";
import {
  ICategoryTag,
  CreateCategoryTagDto,
  UpdateCategoryTagDto,
} from "shared";
import { useLanguage } from "../context/LanguageContext";

const DEFAULT_COLORS = [
  { value: "#1890ff", label: "Blue", color: "#1890ff" },
  { value: "#722ed1", label: "Purple", color: "#722ed1" },
  { value: "#13c2c2", label: "Cyan", color: "#13c2c2" },
  { value: "#eb2f96", label: "Pink", color: "#eb2f96" },
  { value: "#52c41a", label: "Green", color: "#52c41a" },
  { value: "#fa8c16", label: "Orange", color: "#fa8c16" },
  { value: "#f5222d", label: "Red", color: "#f5222d" },
  { value: "#faad14", label: "Yellow", color: "#faad14" },
];

const CategoryTags: React.FC = () => {
  const { t } = useLanguage();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTag, setEditingTag] = useState<ICategoryTag | null>(null);
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [tagToDelete, setTagToDelete] = useState<string | null>(null);
  const queryClient = useQueryClient();

  // Form state
  const [formData, setFormData] = useState<CreateCategoryTagDto>({
    name: "",
    slug: "",
    color: DEFAULT_COLORS[0].value,
    description: "",
  });

  // Fetch category tags
  const {
    data: tags = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ["category-tags"],
    queryFn: () => categoryTagsApi.getAll(),
  });

  // Fetch categories to show usage counts
  const { data: categories = [] } = useQuery({
    queryKey: ["categories"],
    queryFn: () => categoriesApi.getAll(),
  });

  // Calculate tag usage statistics
  const tagStats = useMemo(() => {
    const stats = new Map<string, number>();
    categories.forEach((category) => {
      const count = stats.get(category.tag) || 0;
      stats.set(category.tag, count + 1);
    });
    return stats;
  }, [categories]);

  // Create tag mutation
  const createMutation = useMutation({
    mutationFn: (data: CreateCategoryTagDto) => categoryTagsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["category-tags"] });
      setIsModalOpen(false);
      resetForm();
    },
  });

  // Update tag mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCategoryTagDto }) =>
      categoryTagsApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["category-tags"] });
      setIsModalOpen(false);
      resetForm();
    },
  });

  // Delete tag mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => categoryTagsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["category-tags"] });
      setShowDeleteAlert(false);
      setTagToDelete(null);
    },
  });

  const resetForm = () => {
    setFormData({
      name: "",
      slug: "",
      color: DEFAULT_COLORS[0].value,
      description: "",
    });
    setEditingTag(null);
  };

  const handleOpenModal = (tag?: ICategoryTag) => {
    if (tag) {
      setFormData({
        name: tag.name,
        slug: tag.slug,
        color: tag.color || DEFAULT_COLORS[0].value,
        description: tag.description || "",
      });
      setEditingTag(tag);
    } else {
      resetForm();
    }
    setIsModalOpen(true);
  };

  const handleSubmit = () => {
    if (!formData.name.trim()) return;

    const submitData = {
      ...formData,
      slug: formData.slug || formData.name.toLowerCase().replace(/[^a-z0-9]+/g, "-"),
    };

    if (editingTag) {
      updateMutation.mutate({ id: editingTag.id, data: submitData });
    } else {
      createMutation.mutate(submitData);
    }
  };

  const handleDeleteTag = (id: string) => {
    const categoryCount = tagStats.get(id) || 0;
    if (categoryCount > 0) {
      alert(t('tagCannotBeDeleted') || 'This tag cannot be deleted because it is being used by categories.');
      return;
    }
    setTagToDelete(id);
    setShowDeleteAlert(true);
  };

  const confirmDelete = () => {
    if (tagToDelete) {
      deleteMutation.mutate(tagToDelete);
    }
  };

  const generateSlug = (name: string) => {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, "-");
  };

  const handleNameChange = (value: string) => {
    setFormData(prev => ({
      ...prev,
      name: value,
      slug: prev.slug || generateSlug(value),
    }));
  };

  if (error) {
    return (
      <IonPage>
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="start">
              <IonBackButton defaultHref="/tabs/more" />
            </IonButtons>
            <IonTitle>{t('categoryTagsManagement') || 'Category Tags'}</IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent>
          <div style={{ padding: '20px', textAlign: 'center' }}>
            <p>{t('errorLoadingTags') || 'Error loading category tags'}</p>
            <IonButton fill="clear" onClick={() => window.location.reload()}>
              {t('retry') || 'Retry'}
            </IonButton>
          </div>
        </IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/tabs/more" />
          </IonButtons>
          <IonTitle>{t('categoryTagsManagement') || 'Category Tags'}</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={() => handleOpenModal()}>
              <IonIcon icon={addOutline} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent>
        {/* Statistics Cards */}
        <IonGrid style={{ padding: '16px' }}>
          <IonRow>
            <IonCol size="6">
              <IonCard>
                <IonCardHeader>
                  <IonCardTitle style={{ fontSize: '14px', fontWeight: '600' }}>
                    {t('totalTags') || 'Total Tags'}
                  </IonCardTitle>
                </IonCardHeader>
                <IonCardContent>
                  <h2 style={{ margin: 0, fontSize: '24px', fontWeight: 'bold' }}>
                    {tags.length}
                  </h2>
                </IonCardContent>
              </IonCard>
            </IonCol>
            <IonCol size="6">
              <IonCard>
                <IonCardHeader>
                  <IonCardTitle style={{ fontSize: '14px', fontWeight: '600' }}>
                    {t('usedTags') || 'Used Tags'}
                  </IonCardTitle>
                </IonCardHeader>
                <IonCardContent>
                  <h2 style={{ margin: 0, fontSize: '24px', fontWeight: 'bold' }}>
                    {tagStats.size}
                  </h2>
                </IonCardContent>
              </IonCard>
            </IonCol>
          </IonRow>
        </IonGrid>

        {/* Tags List */}
        {tags.map((tag) => (
          <IonCard key={tag.id} style={{ margin: '8px 16px' }}>
            <IonCardContent>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div
                    style={{
                      width: '24px',
                      height: '24px',
                      borderRadius: '50%',
                      backgroundColor: tag.color || '#ccc',
                    }}
                  />
                  <div>
                    <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>
                      {tag.name}
                    </h3>
                    <p style={{ margin: 0, fontSize: '12px', color: '#666' }}>
                      {tag.slug}
                    </p>
                    {tag.description && (
                      <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#888' }}>
                        {tag.description}
                      </p>
                    )}
                    <div style={{ marginTop: '8px' }}>
                      <IonChip color="medium">
                        <IonLabel>
                          {tagStats.get(tag.slug) || 0} {t('categories') || 'categories'}
                        </IonLabel>
                      </IonChip>
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <IonButton
                    fill="clear"
                    size="small"
                    onClick={() => handleOpenModal(tag)}
                  >
                    <IonIcon icon={createOutline} />
                  </IonButton>
                  <IonButton
                    fill="clear"
                    color="danger"
                    size="small"
                    onClick={() => handleDeleteTag(tag.id)}
                    disabled={(tagStats.get(tag.slug) || 0) > 0}
                  >
                    <IonIcon icon={trashOutline} />
                  </IonButton>
                </div>
              </div>
            </IonCardContent>
          </IonCard>
        ))}

        {tags.length === 0 && !isLoading && (
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <IonIcon
              icon={colorPaletteOutline}
              style={{ fontSize: '64px', color: '#ccc', marginBottom: '16px' }}
            />
            <h3>{t('noCategoryTagsFound') || 'No Category Tags Found'}</h3>
            <p style={{ color: '#666' }}>
              {t('createFirstTagToStart') || 'Create your first category tag to start organizing your categories.'}
            </p>
          </div>
        )}

        <IonLoading isOpen={isLoading || createMutation.isPending || updateMutation.isPending || deleteMutation.isPending} />

        {/* Create/Edit Modal */}
        <IonModal isOpen={isModalOpen} onDidDismiss={() => setIsModalOpen(false)}>
          <IonHeader>
            <IonToolbar>
              <IonTitle>
                {editingTag ? t('editTag') || 'Edit Tag' : t('createTag') || 'Create Tag'}
              </IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setIsModalOpen(false)}>
                  <IonIcon icon={closeOutline} />
                </IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent style={{ padding: '20px' }}>
            <IonItem>
              <IonLabel position="stacked">
                {t('tagName') || 'Tag Name'}
              </IonLabel>
              <IonInput
                value={formData.name}
                onIonInput={(e) => handleNameChange(e.detail.value || "")}
                placeholder={t('enterTagName') || 'Enter tag name'}
              />
            </IonItem>

            <IonItem>
              <IonLabel position="stacked">
                {t('slug') || 'Slug'}
              </IonLabel>
              <IonInput
                value={formData.slug}
                onIonInput={(e) => setFormData(prev => ({ ...prev, slug: e.detail.value || "" }))}
                placeholder={t('urlFriendlySlug') || 'URL-friendly slug'}
              />
            </IonItem>

            <IonItem>
              <IonLabel position="stacked">
                {t('color') || 'Color'}
              </IonLabel>
              <IonSelect
                value={formData.color}
                onIonChange={(e) => setFormData(prev => ({ ...prev, color: e.detail.value }))}
                placeholder={t('selectColor') || 'Select color'}
              >
                {DEFAULT_COLORS.map((color) => (
                  <IonSelectOption key={color.value} value={color.value}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div
                        style={{
                          width: '16px',
                          height: '16px',
                          borderRadius: '50%',
                          backgroundColor: color.color,
                        }}
                      />
                      {color.label}
                    </div>
                  </IonSelectOption>
                ))}
              </IonSelect>
            </IonItem>

            <IonItem>
              <IonLabel position="stacked">
                {t('description') || 'Description'}
              </IonLabel>
              <IonInput
                value={formData.description}
                onIonInput={(e) => setFormData(prev => ({ ...prev, description: e.detail.value || "" }))}
                placeholder={t('tagDescription') || 'Tag description (optional)'}
              />
            </IonItem>

            <div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
              <IonButton
                expand="block"
                fill="outline"
                onClick={() => setIsModalOpen(false)}
              >
                {t('cancel') || 'Cancel'}
              </IonButton>
              <IonButton
                expand="block"
                onClick={handleSubmit}
                disabled={!formData.name.trim() || createMutation.isPending || updateMutation.isPending}
              >
                {editingTag ? t('update') || 'Update' : t('add') || 'Add'}
              </IonButton>
            </div>
          </IonContent>
        </IonModal>

        {/* Delete Alert */}
        <IonAlert
          isOpen={showDeleteAlert}
          onDidDismiss={() => setShowDeleteAlert(false)}
          header={t('confirmDelete') || 'Confirm Delete'}
          message={t('deleteTagWarning') || 'Are you sure you want to delete this tag? This action cannot be undone.'}
          buttons={[
            {
              text: t('cancel') || 'Cancel',
              role: 'cancel',
            },
            {
              text: t('delete') || 'Delete',
              role: 'destructive',
              handler: confirmDelete,
            },
          ]}
        />
      </IonContent>

      <IonFab vertical="bottom" horizontal="end" slot="fixed">
        <IonFabButton onClick={() => handleOpenModal()}>
          <IonIcon icon={addOutline} />
        </IonFabButton>
      </IonFab>
    </IonPage>
  );
};

export default CategoryTags;